import { isArray, isEmpty, isFunction } from 'lodash-es';
import { MagnifyingGlass } from 'phosphor-react';
import type { ChangeEvent, KeyboardEvent, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '../../../debounce';
import type { FormFieldVariant } from '../form-field-variant';
import { InputText } from '../input/input';
import type { EmptyOptionValue, OptionValue, Options, OptionsOrProvider, OptionsProvider } from './option';
import { EMPTY_OPTION, isOption, toOptions } from './option';

const emptyOptions: never[] = [];

export function useOptionsFilter<
  AEO extends boolean
>(
  optionsOrOptionProvider: OptionsOrProvider,
  menuOpen: boolean,
  selectValue: AEO extends true ? ((value: OptionValue | EmptyOptionValue) => void) : (value: OptionValue) => void,
  closeMenu: () => void,
  addEmptyOption?: AEO,
  closeOnSelect = true,
) {
  const [filterQuery, setFilterQuery] = useState<string>('');
  const filterQueryInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasProvider = isFunction(optionsOrOptionProvider);
  const [resolvedOptions, setResolvedOptions] = useState<Options>(hasProvider ? emptyOptions : toOptions(optionsOrOptionProvider));
  const [loading, setLoading] = useState(false);

  const resolveQuery = useCallback(
    async (query: string) => {
      setLoading(true);
      const resolvedOptions = await Promise.resolve((optionsOrOptionProvider as OptionsProvider)(query));
      setLoading(false);
      setResolvedOptions(toOptions(resolvedOptions));
    }, [optionsOrOptionProvider],
  );

  const debouncedResolve = useDebounce(resolveQuery, 500);

  useEffect(() => {
    if (hasProvider) {
      debouncedResolve(filterQuery);
    } else if (isArray(optionsOrOptionProvider)) {
      setResolvedOptions(toOptions(optionsOrOptionProvider));
    } else {
      setResolvedOptions(emptyOptions);
    }
  }, [addEmptyOption, debouncedResolve, filterQuery, hasProvider, optionsOrOptionProvider]);

  const filteredOptions = useMemo(
    () => {
      const withOptionalEmptyOption = (options: Options) => addEmptyOption ? [EMPTY_OPTION, ...options] : options;

      if (hasProvider) {
        return withOptionalEmptyOption(resolvedOptions);
      } else {
        return withOptionalEmptyOption(filterOptions(resolvedOptions, filterQuery));
      }
    },
    [filterQuery, hasProvider, resolvedOptions, addEmptyOption],
  );

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    setTimeout(() => filterQueryInputRef.current?.focus());
  }, [menuOpen]);

  const handleFilterQuery = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setFilterQuery(event.target.value);
  }, []);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      setFilterQuery('');
      closeMenu();
    }
    if (event.key === 'ArrowDown') {
      contentRef.current?.querySelector<HTMLDivElement>('[role="menuitemradio"], [role="menuitemcheckbox"]')?.focus();
    }
  }, [closeMenu]);

  const handleSearchKeyUp = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter' && filteredOptions.length === 1) {
      selectValue(filteredOptions[0].value as OptionValue);
      setFilterQuery('');

      if (closeOnSelect) {
        closeMenu();
      }
    }
  }, [closeMenu, closeOnSelect, filteredOptions, selectValue]);

  const filteredItemStyle = {
    '!bg-primary text-contrast': filteredOptions.length === 1 && !isEmpty(filterQuery),
  };

  return {
    filteredOptions,
    filteredItemStyle,
    contentRef,
    loading,
    optionsFilterProps: {
      filterQueryInputRef,
      filterQuery,
      handleFilterQuery,
      handleSearchKeyDown,
      handleSearchKeyUp,
    },
  };
}

type OptionsFilterProps = {
  filterQueryInputRef: RefObject<HTMLInputElement | null>;
  filterQuery: string;
  handleFilterQuery: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleSearchKeyUp: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  variant: FormFieldVariant;
};

export const OptionsFilter = ({ filterQueryInputRef, filterQuery, placeholder, handleFilterQuery, handleSearchKeyDown, handleSearchKeyUp, variant }: OptionsFilterProps) => (
  <span className="flex items-center border-b border-b-light">
    {/*
      In addition to autofocus, which is fast but for some reason does
      not work in modals, we also use an effect to focus the input
      imperatively.
    */}
    <InputText
      placeholder={placeholder}
      className="w-full !border-none !rounded-b-none"
      autoFocus
      inputRef={filterQueryInputRef as RefObject<HTMLInputElement>}
      value={filterQuery}
      onChange={handleFilterQuery}
      onKeyPress={e => e.stopPropagation()}
      onKeyDown={handleSearchKeyDown}
      onKeyUp={handleSearchKeyUp}
      variant={variant}
    />
    <span className="pr-3 text-medium -ml-7">
      <MagnifyingGlass size={16} className="h-full" />
    </span>
  </span>
);

function filterOptions(options: Options, filterQuery: string) {
  if (isEmpty(filterQuery)) {
    return options;
  }

  return options
    .filter(option => option.label.toLowerCase().includes(filterQuery.toLowerCase()))
    .filter(isOption);
}
